import {
    faBalanceScale,
    faChartLine,
    faFileInvoiceDollar,
    faPlusCircle,
    faRulerCombined,
    faSitemap
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { formatCurrencyToLocale } from "../../../../utils/method";
import "./CostSummaryCards.scss";

function CostSummaryCards({ summary, showGross = true }) {
    const { t } = useTranslation();

    const supplementPercentage = summary?.totalAward > 0
        ? ((summary?.totalSupplement || 0) / summary?.totalAward) * 100
        : 0;

    const aggregatedTotal = (summary?.totalAward || 0) + (summary?.totalSupplement || 0);

    const measurementProgress = aggregatedTotal > 0
        ? Math.min(((summary?.totalMeasured || 0) / aggregatedTotal) * 100, 100)
        : 0;

    const budgetVariancePercentage = aggregatedTotal > 0
        ? ((summary?.budgetVariance || 0) / aggregatedTotal) * 100
        : 0;

    const getActualVariant = () => {
        if (Math.abs(budgetVariancePercentage) <= 5) return "actual-ok";
        return budgetVariancePercentage > 0 ? "actual-danger" : "actual-warn";
    };

    return (
        <div className="kpi-cards-strip">

            <div className="kpi-card kpi-card--ordered">
                <div className="kpi-card__icon">
                    <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div className="kpi-card__body">
                    <div className="kpi-card__label">{t("costTracking.ordered-sum")}</div>
                    <div className="kpi-card__value">
                        {formatCurrencyToLocale(summary?.totalAward || 0)}
                    </div>
                    <div className="kpi-card__sub">{t("costTracking.planned-budget")}</div>
                </div>
            </div>

            <div className="kpi-card kpi-card--supplements">
                <div className="kpi-card__icon">
                    <FontAwesomeIcon icon={faPlusCircle} />
                </div>
                <div className="kpi-card__body">
                    <div className="kpi-card__label">{t("costTracking.supplements-approved")}</div>
                    <div className="kpi-card__value">
                        {formatCurrencyToLocale(summary?.totalSupplement || 0)}
                    </div>
                    <div className="kpi-card__sub kpi-card__sub--warn">
                        {supplementPercentage.toFixed(1)}% {t("costTracking.of-budget")}
                    </div>
                </div>
            </div>

            <div className="kpi-card kpi-card--aggregated">
                <div className="kpi-card__icon">
                    <FontAwesomeIcon icon={faSitemap} />
                </div>
                <div className="kpi-card__body">
                    <div className="kpi-card__label">{t("costTracking.aggregated-lv-na")}</div>
                    <div className="kpi-card__value">{formatCurrencyToLocale(aggregatedTotal)}</div>
                    <div className="kpi-card__sub">
                        {t("costTracking.ordered-sum")} + {t("costTracking.supplements-approved")}
                    </div>
                </div>
            </div>

            <div className={`kpi-card kpi-card--${getActualVariant()}`}>
                <div className="kpi-card__icon">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} />
                </div>
                <div className="kpi-card__body">
                    <div className="kpi-card__label">{t("costTracking.actual-invoiced")}</div>
                    <div className="kpi-card__value">
                        {formatCurrencyToLocale(summary?.totalActual || 0)}
                    </div>
                </div>
            </div>

            <div className="kpi-card kpi-card--measured">
                <div className="kpi-card__icon">
                    <FontAwesomeIcon icon={faRulerCombined} />
                </div>
                <div className="kpi-card__body">
                    <div className="kpi-card__label">{t("costTracking.measured")}</div>
                    <div className="kpi-card__value">
                        {formatCurrencyToLocale(summary?.totalMeasured || 0)}
                    </div>
                    <div className="kpi-card__progress">
                        <div className="kpi-card__progress-fill" style={{ width: `${measurementProgress}%` }} />
                    </div>
                </div>
            </div>

            <div className="kpi-card kpi-card--released">
                <div className="kpi-card__icon">
                    <FontAwesomeIcon icon={faBalanceScale} />
                </div>
                <div className="kpi-card__body">
                    <div className="kpi-card__label">{t("costTracking.total-released")}</div>
                    <div className="kpi-card__value">
                        {formatCurrencyToLocale(showGross ? (summary?.totalReleased || 0) : (summary?.totalReleasedNet || 0))}
                    </div>
                    <div className="kpi-card__sub">
                        {showGross ? t("supplier-info.total-gross") : t("costTracking.netto")}
                    </div>
                </div>
            </div>

        </div>
    );
}

export default CostSummaryCards;
